import {
  Add,
  ApiKey,
  Building,
  Checkmark,
  Cookie,
  Home,
  IdManagement,
  Logout,
  Purchase,
  TrashCan,
  User,
} from '@carbon/icons-react';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  NavbarMenuItem,
  NavbarMenuItems,
  NavbarSchema,
} from 'vet-bones/components';
import { cookiebotAgent } from 'vet-bones/utils';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  NAVBAR_AVATAR_MENU,
  NAVBAR_AVATAR_MENU_ITEM,
} from 'src/ui/constants/testIds';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  SavedConnectionsDocument,
  SavedConnectionsQuery,
  useGetStripeSessionUrlMutation,
  useSavedConnectionsQuery,
} from 'src/ui/graph/types';
import {
  getLastConnectionIndex,
  useProfile,
  useRecordLastWorkspace,
} from 'src/ui/hooks/connection';
import {
  resolveConnectionIdx,
  useOrgDomain,
} from 'src/ui/hooks/navbar/connection';
import { useListOrganizations } from 'src/ui/hooks/organization';
import { loggedInUserEmail } from 'src/ui/selectors/loggedInUserEmail';
import * as copy from 'src/ui/templates/copy';
import { buildDashboardRoute, buildRoute } from 'src/ui/utils/buildRoute';
import { isPublicApiV2Enabled } from 'src/ui/utils/features';
import { redirectToUrl } from 'src/ui/utils/window';

export const usePortalNavbarAvatarSchema = (): NonNullable<
  NavbarSchema['footer']
>['avatar'] => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const orgDomain = useOrgDomain();
  const email = useAppSelector(loggedInUserEmail);

  const { data: profileData } = useProfile();
  const profile = profileData?.profile;

  const { data: organizationsData } = useListOrganizations();
  const organizations = organizationsData?.listOrganizations;

  const { mutate: mutateStripeSession } = useGetStripeSessionUrlMutation(
    graphQLClient,
    {
      onSuccess: (data) => {
        redirectToUrl(
          data?.getStripeSessionUrl?.url || buildDashboardRoute(orgDomain)
        );
      },
    }
  );

  const handleUpdateProfile = React.useCallback(() => {
    dispatch(openedDialog({ type: DialogType.UPDATE_PROFILE }));
  }, [dispatch]);

  const handleManageBilling = React.useCallback(() => {
    mutateStripeSession({});
  }, [mutateStripeSession]);

  const handleManageApiKeys = React.useCallback(() => {
    history.push('/keys');
  }, [history]);

  const handleLogout = React.useCallback(() => {
    redirectToUrl('/auth/logout');
  }, []);

  const handleEmployeeHome = React.useCallback(() => {
    history.push('/internal');
  }, [history]);

  const recordLastWorkspace = useRecordLastWorkspace();
  // a ref rather than state: the check after the await has to read the newest
  // token, and a state value would be frozen at whatever the closure captured
  const selectionTokenRef = React.useRef(0);
  const handleSelectOrg = React.useCallback(
    async (domain: string) => {
      // claim the latest selection, superseding any lookup still in flight.
      // this runs before the personal workspace path below so that choice
      // supersedes a pending org lookup too
      selectionTokenRef.current += 1;
      const token = selectionTokenRef.current;

      // record the choice before any async work so the root landing honors it
      // even if the endpoint lookup below fails
      recordLastWorkspace(domain);

      if (!domain) {
        // the personal workspace landing resolves its own last used endpoint
        history.push('/');
        return;
      }

      let connectionIdx = '';
      try {
        // warms the cache the tools page reads on arrival, keyed the same way
        const data = await queryClient.fetchQuery(
          useSavedConnectionsQuery.getKey({ org_domain: domain }),
          () =>
            graphQLClient.request<SavedConnectionsQuery>({
              document: SavedConnectionsDocument,
              variables: { org_domain: domain },
            })
        );
        connectionIdx = resolveConnectionIdx(
          getLastConnectionIndex(email, domain),
          data.listConnections
        );
      } catch {
        // leave the index empty so we land on the org dashboard instead
      }

      if (selectionTokenRef.current !== token) {
        // a newer selection has already navigated; don't clobber its destination
        return;
      }

      // an empty index routes to the org dashboard
      history.push(buildRoute(domain, connectionIdx));
    },
    [email, history, queryClient, recordLastWorkspace]
  );

  const handleAddOrganization = React.useCallback(() => {
    dispatch(openedDialog({ type: DialogType.ADD_ORGANIZATION }));
  }, [dispatch]);

  const handleDeleteAccount = React.useCallback(() => {
    dispatch(openedDialog({ type: DialogType.DELETE_ACCOUNT }));
  }, [dispatch]);

  return React.useMemo(() => {
    if (!profile || !profile.is_authenticated) {
      return undefined;
    }

    const firstName = profile.first_name || profile.username || '';
    const lastName = profile.last_name || '';
    const orgName =
      organizations?.find((org) => org?.domain === orgDomain)?.name || '';

    const items: NavbarMenuItem[] = [
      {
        icon: User,
        label: copy.components.navbar.avatar.menuItems.updateProfile,
        onClick: handleUpdateProfile,
        testId: NAVBAR_AVATAR_MENU_ITEM('update-profile'),
      },
    ];

    if (profile.stripe_customer?.customer_id) {
      items.push({
        icon: Purchase,
        label: copy.components.navbar.avatar.menuItems.manageBilling,
        onClick: handleManageBilling,
        testId: NAVBAR_AVATAR_MENU_ITEM('manage-billing'),
      });
    }

    if (
      (isPublicApiV2Enabled() || profile.is_voicebox_api_access_enabled) &&
      !orgDomain
    ) {
      items.push({
        icon: ApiKey,
        label: copy.components.navbar.avatar.menuItems.manageApiTokens,
        onClick: handleManageApiKeys,
        testId: NAVBAR_AVATAR_MENU_ITEM('manage-api-tokens'),
      });
    }

    if (cookiebotAgent.loaded) {
      items.push({
        icon: Cookie,
        label: copy.components.navbar.avatar.menuItems.updateCookieConsent,
        onClick: () => cookiebotAgent.openDialog(),
        testId: NAVBAR_AVATAR_MENU_ITEM('update-cookie-consent'),
      });
    }

    items.push({
      icon: Logout,
      label: copy.components.navbar.avatar.menuItems.logout,
      onClick: handleLogout,
      testId: NAVBAR_AVATAR_MENU_ITEM('logout'),
    });

    const menuItems: NavbarMenuItems = [{ items }];

    const orgItems: NavbarMenuItem[] = [
      {
        icon: Home,
        label: copy.components.navbar.avatar.menuItems.personalOrganization,
        onClick: () => handleSelectOrg(''),
        rightIcon: orgDomain ? undefined : Checkmark,
        testId: NAVBAR_AVATAR_MENU_ITEM('personal-org'),
      },
      ...(organizations || [])
        .filter((org): org is {
          id: string;
          name: string;
          domain: string;
        } => Boolean(org?.domain))
        .map((org) => ({
          icon: Building,
          label: org.name,
          onClick: () => handleSelectOrg(org.domain),
          rightIcon: org.domain === orgDomain ? Checkmark : undefined,
          testId: NAVBAR_AVATAR_MENU_ITEM(`public-org-${org.domain}`),
        })),
    ];

    if (profile.is_staff) {
      orgItems.push({
        icon: Add,
        label: copy.components.navbar.avatar.menuItems.addOrganization,
        onClick: handleAddOrganization,
        testId: NAVBAR_AVATAR_MENU_ITEM('add-organization'),
      });
    }

    menuItems.push({ items: orgItems });

    if (profile.is_staff) {
      menuItems.push({
        items: [
          {
            icon: IdManagement,
            label: copy.components.navbar.avatar.menuItems.employeeHome,
            onClick: handleEmployeeHome,
            testId: NAVBAR_AVATAR_MENU_ITEM('employee-home'),
          },
        ],
      });
    }

    menuItems.push({
      items: [
        {
          icon: TrashCan,
          label: copy.components.navbar.avatar.menuItems.deleteAccount,
          onClick: handleDeleteAccount,
          testId: NAVBAR_AVATAR_MENU_ITEM('delete-account'),
        },
      ],
    });

    return {
      description: orgName,
      label: `${firstName} ${lastName}`,
      menuItems,
      testId: NAVBAR_AVATAR_MENU,
      text: `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`,
    };
  }, [
    handleAddOrganization,
    handleDeleteAccount,
    handleEmployeeHome,
    handleLogout,
    handleManageApiKeys,
    handleManageBilling,
    handleSelectOrg,
    handleUpdateProfile,
    orgDomain,
    organizations,
    profile,
  ]);
};
